import React, { useState, useEffect } from "react";
import VectorGround from "./vectors/VectorGround";
import VectorMountainsFar from "./vectors/VectorMountainsFar";
import VectorLand from "./vectors/VectorLand";
import VectorLantern from "./vectors/VectorLantern";
import Star from "./vectors/Star";
import FallingLeaf, { LEAF_EMOJIS } from "./vectors/FallingLeaf";
import "./Themes.scss";

const MAX_STARS = 35;
const MAX_LEAVES = 25;

export interface StarConfig {
    id: number;
    left: number;
    top: number;
    delay: number;
}

export interface LeafConfig {
    id: number;
    left: number;
    size: number;
    duration: number;
    direction: number;
    emoji: string;
}

const ThemeAutumn = ({ children }: { children?: React.ReactNode }) => {
    const [stars, setStars] = useState<StarConfig[]>([]);
    const [leaves, setLeaves] = useState<LeafConfig[]>([]);
    const [score, setScore] = useState<number>(0);
    const [isFocusMode, setIsFocusMode] = useState<boolean>(false);

    useEffect(() => {
        // Tạo bầu trời đầy sao nhấp nháy
        const initialStars: StarConfig[] = Array.from({ length: MAX_STARS }, (_, i) => ({
            id: i,
            left: Math.random() * 100,
            top: Math.random() * 75,
            delay: Math.random() * 4,
        }));
        setStars(initialStars);

        // Khởi tạo lá rơi rải rác
        const initialLeaves: LeafConfig[] = Array.from({ length: 8 }, (_, i) => ({
            id: i,
            left: Math.random() * 95,
            size: 20 + Math.random() * 18,
            duration: 8 + Math.random() * 10,
            direction: (Math.random() - 0.5) * 160,
            emoji: LEAF_EMOJIS[Math.floor(Math.random() * LEAF_EMOJIS.length)]
        }));
        setLeaves(initialLeaves);

        // Sinh lá thu mới rơi rải rác (🍁, 🍂, 🏮)
        const leafInterval = setInterval(() => {
            setLeaves((prevLeaves) => {
                if (prevLeaves.length >= MAX_LEAVES) return prevLeaves;
                const autumnEmojis = ["🍁", "🍂", "🏮"];
                const newLeaf: LeafConfig = {
                    id: Date.now() + Math.random(),
                    left: Math.random() * 95,
                    size: 20 + Math.random() * 18,
                    duration: 7.5 + Math.random() * 10,
                    direction: (Math.random() - 0.5) * 160,
                    emoji: autumnEmojis[Math.floor(Math.random() * autumnEmojis.length)]
                };
                return [...prevLeaves, newLeaf];
            });
        }, 2000);

        // Sinh bánh trung thu (🥮) riêng biệt
        const mooncakeInterval = setInterval(() => {
            setLeaves((prevLeaves) => {
                if (prevLeaves.length >= MAX_LEAVES) return prevLeaves;
                const newMooncake: LeafConfig = {
                    id: Date.now() + Math.random(),
                    left: Math.random() * 95,
                    size: 30 + Math.random() * 20,
                    duration: 7 + Math.random() * 10,
                    direction: (Math.random() - 0.5) * 140,
                    emoji: "🥮"
                };
                return [...prevLeaves, newMooncake];
            });
        }, 900);

        return () => {
            clearInterval(leafInterval);
            clearInterval(mooncakeInterval);
        };
    }, []);

    const removeLeaf = (id: number) => {
        setLeaves((prev) => prev.filter((leaf) => leaf.id !== id));
    };

    const handleCatchLeaf = () => {
        setScore((prev) => prev + 1);
    };

    const formatScore = (num: number) => {
        return num.toLocaleString("vi-VN");
    };

    return (
        <div className={`theme-vectors-container is-theme-autumn ${isFocusMode ? "is-focus-mode" : ""}`}>
            {children}

            <div className="nav-body-vectors">
                {/* Bầu trời sao nhấp nháy */}
                {stars.map((star) => (
                    <Star key={star.id} left={star.left} top={star.top} delay={star.delay} />
                ))}

                {/* Dãy núi phía xa */}
                <VectorMountainsFar className="vector-mountains-far" />

                {/* Đất thu */}
                <VectorLand className="vector-land" />

                {/* Mặt đất */}
                <VectorGround className="vector-ground" />

                {/* 1 Lồng đèn góc trái duy nhất (Phong cách Flat Vector tối giản) */}
                <VectorLantern className="vector-lantern cursor-pointer lantern-single-left" stringLength={22} />

                {/* Nút bấm chuyển chế độ chơi Catch the cake */}
                <button
                    type="button"
                    className={`autumn-focus-btn ${isFocusMode ? "active" : ""}`}
                    onClick={() => setIsFocusMode(!isFocusMode)}
                >
                    {isFocusMode ? "Quit" : "Catch the cake 🥮"}
                </button>

                {/* Điểm số ở Bottom Center (Ban đầu là 🥮 0) */}
                {isFocusMode && <div className="autumn-score-box">
                    <span style={{ fontSize: 18 }}>🥮</span> <span key={score} className="score-number">x{formatScore(score)}</span>
                </div>}
            </div>

            {/* Lớp lá rơi hiệu ứng nằm trên cùng (Click tích điểm & fade out) */}
            <div className="falling-leaves-layer">
                {leaves.map((leaf) => (
                    <FallingLeaf
                        key={leaf.id}
                        id={leaf.id}
                        left={leaf.left}
                        size={leaf.size}
                        duration={leaf.duration}
                        direction={leaf.direction}
                        emoji={leaf.emoji}
                        isGameActive={isFocusMode}
                        onComplete={removeLeaf}
                        onCatch={handleCatchLeaf}
                    />
                ))}
            </div>
        </div>
    );
};

export default ThemeAutumn;
